import { useEffect, useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { Mail, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/Button'
import { marketingService, type NewsletterPreference } from '@/services/marketing'
import { authService } from '@/services/auth'
export function NewsletterPrompt() {
  const { user, updateUser } = useAuth()
  const { t } = useTranslation()
  const [preference, setPreference] = useState<NewsletterPreference | null>(null)
  const [dismissed, setDismissed] = useState(false)
  // The choice being saved, so only the clicked button shows progress.
  const [pending, setPending] = useState<boolean | null>(null)
  const [error, setError] = useState(false)
  const busy = pending !== null
  useEffect(() => {
    let active = true; setPreference(null); setError(false)
    let alreadyDismissed = false
    try { alreadyDismissed = sessionStorage.getItem(`newsletter-dismissed:${user?.id}`) === authService.getToken() } catch { /* Local state still works. */ }
    setDismissed(alreadyDismissed)
    if (user?.id) marketingService.getNewsletter().then(value => { if (active) setPreference(value) }).catch(() => {})
    return () => { active = false }
  }, [user?.id])
  const close = () => {
    if (busy) return
    setDismissed(true)
    try { sessionStorage.setItem(`newsletter-dismissed:${user?.id}`, authService.getToken() ?? '') } catch { /* No persistent dismissal if storage is unavailable. */ }
  }
  const choose = async (subscribed: boolean) => {
    setPending(subscribed); setError(false)
    try { const next = await marketingService.setNewsletter(subscribed, 'dashboard_prompt'); setPreference(next); updateUser(next) }
    catch { setError(true) } finally { setPending(null) }
  }
  return <Dialog open={Boolean(preference?.needs_newsletter_prompt && !dismissed)} onClose={close} transition className="fixed inset-0 z-50">
    <DialogBackdrop transition className="fixed inset-0 bg-gray-900/40 transition-opacity duration-200 ease-out data-[closed]:opacity-0" />
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
      <DialogPanel transition className="relative w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-xl transition duration-200 ease-out data-[closed]:translate-y-3 data-[closed]:opacity-0 dark:border-gray-800 dark:bg-gray-900">
        <button type="button" aria-label={t('newsletter.close')} disabled={busy} onClick={close} className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300" aria-hidden="true"><Mail className="h-5 w-5" /></div>
        <DialogTitle className="mt-4 pr-10 text-lg font-semibold text-gray-900 dark:text-white">{t('newsletter.title')}</DialogTitle>
        <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200">{t('newsletter.consent')}</p>
        <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{t('newsletter.optional')}</p>
        {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{t('newsletter.error')}</p>}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" disabled={busy && pending !== false} isLoading={pending === false} onClick={() => choose(false)}>{t('newsletter.decline')}</Button>
          <Button disabled={busy && pending !== true} isLoading={pending === true} onClick={() => choose(true)}>{t('newsletter.accept')}</Button>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
}
